import { isValidEmail } from "@repo/app-types/helpers/utilities";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Input } from "@repo/ui/Inputs/input";
import type { FC } from "react";

interface EmailMagicLinkFormProps {
  email: string;
  isSending: boolean;
  /** The address a link was just sent to, which switches to the confirmation. */
  sentToEmail: string | null;
  errorMessage: string | null;
  onEmailChange: (value: string) => void;
  onSubmit: () => void;
  onReset: () => void;
}

/**
 * "Work email" input that sends a Supabase magic link, plus the post-send
 * confirmation. Presentational — shared by the standalone sign-in screen and the
 * invitation sign-in screen (via `AuthMethodOptions`).
 */
export const EmailMagicLinkForm: FC<EmailMagicLinkFormProps> = ({
  email,
  isSending,
  sentToEmail,
  errorMessage,
  onEmailChange,
  onSubmit,
  onReset,
}) => {
  if (sentToEmail) {
    return (
      <div className="w-full text-center">
        <p className="text-sm text-foreground">
          We sent a link to{" "}
          <span className="font-medium text-foreground">{sentToEmail}</span>.
          Click the link in your email to finish signing in.
        </p>
        <p className="mt-3 text-xs text-muted-foreground">
          Didn&apos;t receive it? Check your spam folder, or{" "}
          <button
            className="font-medium text-blue-600 hover:text-blue-500"
            onClick={onReset}
          >
            try a different email
          </button>
          .
        </p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <Input
        label="Work email"
        placeholder="you@company.com"
        value={email}
        autoComplete="email"
        autoCapitalize="off"
        autoCorrect="off"
        onChange={(e) => onEmailChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && isValidEmail(email)) {
            onSubmit();
          }
        }}
        maxLength={100}
      />
      {errorMessage ? (
        <p className="mt-2 text-sm text-destructive">{errorMessage}</p>
      ) : null}
      <Button
        className="mx-auto mt-4"
        onClick={onSubmit}
        variant={ButtonVariant.Primary}
        isDisabled={!isValidEmail(email) || isSending}
      >
        {isSending ? "Sending..." : "Send link"}
      </Button>
    </div>
  );
};
